import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IconSparkles } from '@tabler/icons-react'
import { Formik, Field } from 'formik'

import { toast } from '@/components/toasts/toast.helpers'
import { closeModal } from '@/reducers/ui/ui.redux'
import { editScorecardConfig } from '@/reducers/qa-copilot/qa-copilot.redux'
import { fetchPersonaList, generateCopilotPrompt } from '@/reducers/prompts/prompts.actions'
import { Select } from '@/components/forms/formik'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'

export const QACopilotPromptGenerationForm = ({
  setFieldValue: setParentFieldValue,
  criterionId,
}) => {
  const dispatch = useDispatch()
  const scorecardConfig = useSelector((state) => state.qaCopilot.scorecardConfig)
  const personaOptions = useSelector((state) => state.prompts.personaOptions) ?? []
  const loadingPersonaList = useSelector((state) => state.prompts.loading.personaOptions)

  useEffect(() => {
    dispatch(fetchPersonaList())
  }, [dispatch])

  const handleGeneratePrompt = async (values, { setSubmitting }) => {
    try {
      const response = await dispatch(
        generateCopilotPrompt(
          values.copilotPersona,
          values.question,
          values.context,
          values.applicable,
          values.notApplicable,
          values.passExample
        )
      )

      if (response?.generated_prompt) {
        // Update local form
        setParentFieldValue('user_prompt', response.generated_prompt)

        // Update Redux scorecard config
        const updatedSections = scorecardConfig.sections.map((section) => ({
          ...section,
          measures: section.measures.map((measure) => ({
            ...measure,
            criteria: measure.criteria.map((criterion) => {
              if (criterion.scid === criterionId) {
                return { ...criterion, user_prompt: response.generated_prompt }
              }
              return criterion
            }),
          })),
        }))

        dispatch(
          editScorecardConfig({
            ...scorecardConfig,
            sections: updatedSections,
          })
        )

        dispatch(closeModal())
      }
    } catch (error) {
      toast.error('No prompt was returned. Please adjust inputs and try again.', { cause: error })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{
        copilotPersona: '',
        question: '',
        context: '',
        applicable: '',
        notApplicable: '',
        passExample: '',
      }}
      onSubmit={handleGeneratePrompt}
    >
      {({ handleChange, handleSubmit, isSubmitting, values }) => (
        <form className="field-stack" onSubmit={handleSubmit}>
          <FormField>
            <label>Copilot Persona</label>
            <Field
              name="copilotPersona"
              component={Select}
              options={personaOptions}
              placeholder="Select a Copilot Persona"
              disabled={loadingPersonaList}
            />
          </FormField>

          <FormField>
            <label>Question</label>
            <TextArea
              name="question"
              placeholder="Enter the specific question to be evaluated."
              rows={2}
              value={values.question}
              onChange={handleChange}
            />
          </FormField>

          <FormField>
            <label>Context</label>
            <TextArea
              name="context"
              placeholder="Provide background or context for this question."
              rows={3}
              value={values.context}
              onChange={handleChange}
            />
          </FormField>

          <FormField>
            <label>
              When Applicable <span className="normal-weight">(Optional)</span>
            </label>
            <TextArea
              name="applicable"
              placeholder="Describe scenarios where this question applies."
              rows={2}
              value={values.applicable}
              onChange={handleChange}
            />
          </FormField>

          <FormField>
            <label>
              When Not Applicable <span className="normal-weight">(Optional)</span>
            </label>
            <TextArea
              name="notApplicable"
              placeholder="Describe scenarios where this question does not apply."
              rows={2}
              value={values.notApplicable}
              onChange={handleChange}
            />
          </FormField>

          <FormField>
            <label>
              Passing Example <span className="normal-weight">(Optional)</span>
            </label>
            <TextArea
              name="passExample"
              placeholder="Add a clear example that would result in a passing evaluation."
              rows={4}
              value={values.passExample}
              onChange={handleChange}
            />
          </FormField>

          <div className="modal-footer">
            <Button type="button" onClick={() => dispatch(closeModal())}>
              Cancel
            </Button>
            <Button
              loading={isSubmitting}
              type="submit"
              brand
              disabled={
                isSubmitting ||
                !values.question.trim() ||
                !values.copilotPersona ||
                personaOptions.length === 0
              }
            >
              <IconSparkles />
              Generate
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}
